import moment from 'moment-timezone';

/**
 * Selection of frequently used Date Formats
 *
 * Custom formats using Intl.DateTimeFormat:
 * - `DayMonthYear`: Date only, e.g., "15 October 2025"
 * - `DayMonthYearHourMinutes`: Full date and time, e.g., "15 October 2025, 14:30" or "15 October 2025, 2:30 PM"
 * - `DayMonthYearHourMinuteSeconds`: Full date and time with seconds, e.g., "15 Oct 2025, 14:30:45" or "15 Oct 2025, 2:30:45 PM"
 * - `HourMinuteSeconds`: Time with seconds, e.g., "14:30:45" or "2:30:45 PM"
 * - `WeekdayDayMonthYearHourMinutes`: Includes weekday, e.g., "Tuesday, 15 October 2025, 14:30" or "Tuesday, 15 October 2025, 2:30 PM"
 * - `ShortWeekdayDayMonthYearHourMinutes`: Abbreviated weekday and month, e.g., "Tue, 15 Oct 2025, 14:30" or "Tue, 15 Oct 2025, 2:30 PM"
 * - `ShortDayMonthYearHourMinutes`: All numeric format, e.g., "15/10/2025, 14:30" or "15/10/2025, 2:30 PM"
 * - `CompactDayMonthYearHourMinutes`: Compact with short month and 2-digit year, e.g., "15 Oct 25, 14:30" or "15 Oct 25, 2:30 PM"
 * - `Timecode`: Time as timecode with seconds, e.g., "14:30:45" or "2:30:45 PM"
 * - `TimecodeShort`: Time as timecode without seconds, e.g., "14:30" or "2:30 PM"
 * - `ISO`: ISO 8601 format without timezone suffix, e.g., "2025-10-15T14:30:45"
 *
 * Moment.js string formats (see https://momentjs.com/docs/#/displaying/format/):
 */
export type DateFormats =
  | 'LLL' // e.g., "October 15, 2025 2:30 PM"
  | 'YYYY-MM-DD HH:mm:ss' // e.g., "2025-10-15 14:30:45"
  | 'MMMM Do YYYY, h:mm:ss A' // e.g., "October 15th 2025, 2:30:45 PM"
  | 'YYYY-MM-DD' // e.g., "2025-10-15"
  | 'D MMMM, YYYY hh:mm A' // e.g., "15 October, 2025 02:30 PM"
  | 'DD/MM/YYYY' // e.g., "15/10/2025"
  | 'HH:mm - L' // e.g., "14:30 - 10/15/2025"
  | 'HH:mm' // e.g., "14:30"
  | 'LLLL' // e.g., "Tuesday, October 15, 2025 2:30 PM"
  | 'D MMMM, YYYY HH:mm' // e.g., "15 October, 2025 14:30"
  | 'DD/MMM/YY' // e.g., "15/Oct/25"
  | 'DD MMM YYYY, hh:mm A' // e.g., "15 Oct 2025, 02:30 PM"
  | 'DD/MMM/YY HH:mm' // e.g., "15/Oct/25 14:30"
  | 'D MMMM, hh:mm A' // e.g., "15 October, 02:30 PM"
  | 'MMMM Do YYYY' // e.g., "October 15th 2025"
  | 'YYYY' // e.g., "2025"
  | 'MMMM YYYY' // e.g., "October 2025"
  | 'DD MMM YYYY, HH:mm:ss' // e.g., "15 Oct 2025, 14:30:45"
  | 'D MMMM YYYY' // e.g., "15 October 2025"
  | 'HH:mm:ss' // e.g., "14:30:45"
  | 'DayMonthYear' // e.g., "15 October 2025"
  | 'DayMonthYearHourMinutes' // e.g., "15 October 2025 14 30"
  | 'DayMonthYearHourMinuteSeconds' // e.g., "15 October 2025 14 30 45"
  | 'HourMinuteSeconds' // e.g., "14 30 45"
  | 'Timecode' // e.g., "14:30:45"
  | 'TimecodeShort' // e.g., "14:30"
  | 'ISO' // e.g., "2025-10-15T14:30:45"
  | 'WeekdayDayMonthYearHourMinutes' // e.g., "Tuesday, 15 October 2025 14:30"
  | 'ShortWeekdayDayMonthYearHourMinutes' // e.g., "Tue, 15 Oct 2025 14:30"
  | 'ShortDayMonthYearHourMinutes' // e.g., "15/10/2025 14:30"
  | 'CompactDayMonthYearHourMinutes' // e.g., "15 Oct 25 14:30"
  | 'FullDateTimeWithTimezone' // e.g., "Mon Nov 24 2025 13:33:08 GMT+0100"
  | 'CompactDate' // e.g., "15 Oct 2025"
  | 'ShortDate'; // e.g., "15/10/2025"


export const TimeFormat = {
  TWELVE_HOUR: '12h',
  TWENTY_FOUR_HOUR: '24h',
  SYSTEM: 'system',
} as const;

export type TenantTimeFormat = typeof TimeFormat[keyof typeof TimeFormat];

/**
 * DateFormatterOptions is used to configure the date formatter
 * @param {DateFormats} format - specify a format if needed, default is 'D MMMM YYYY hh:mm'
 * @param timeZone - add a timezone if needed, browser will guesstimate timezone if undefined
 * @param locale - add a locale if needed, uses navigator.language if undefined
 * @param hour12 - set this to enforce 12h or 24h clock, leave undefined for locale to control it
 */
interface DateFormatterOptions {
  format?: DateFormats;
  timeZone?: string;
  locale?: string;
  hour12?: boolean;
}

/**
 * Checks if a date value is valid for formatting.
 */
function isValidDateValue(date: string | number | Date | null | undefined): boolean {
  if (date === null || date === undefined || date === '') {
    return false;
  }
  const dateObj = new Date(date);
  return !isNaN(dateObj.getTime());
}

/**
 * This date helper class takes care of configured timezone
 */
export class DateHelper {
  private timezone: string | undefined = undefined;
  private locale: string | undefined = undefined;
  private tenantTimeFormat: TenantTimeFormat | undefined = undefined;

  constructor(timezone?: string, locale?: string) {
    this.timezone = timezone;
    this.locale = locale;
  }

  /**
   * Get the explicitly configured timezone (or undefined if not set)
   */
  getTimezone() {
    return this.timezone;
  }

  /**
   * Get the effective timezone being used for date operations.
   * Returns the configured timezone if set, otherwise detects the browser's timezone.
   * This respects browser sensor overrides and matches what moment.js uses.
   */
  getEffectiveTimezone(): string {
    if (this.timezone) {
      return this.timezone;
    }
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (intlError) {
      return 'UTC';
    }
  }

  getLocale() {
    return this.locale;
  }

  format(date: string | number | Date, options?: DateFormatterOptions) {
    if (date === undefined) {
      date = new Date();
    }

    if (!isValidDateValue(date)) {
      return 'Invalid date';
    }

    const { format, timeZone, locale, hour12 } = { ...options };
    const tz = timeZone ?? this.timezone ?? undefined;
    const lc = locale ?? this.locale ?? undefined;

    let hr12: boolean | undefined;
    if (typeof hour12 === 'boolean') {
      hr12 = hour12;
    } else if (this.tenantTimeFormat && this.tenantTimeFormat !== TimeFormat.SYSTEM) {
      hr12 = this.tenantTimeFormat === TimeFormat.TWELVE_HOUR;
    } else {
      hr12 = undefined;
    }

    if (format) {
      switch (format) {
        case 'WeekdayDayMonthYearHourMinutes':
          return formatDateAndTimeWithWeekday(date, tz, lc, hr12);
        case 'ShortWeekdayDayMonthYearHourMinutes':
          return formatShortDateAndTimeWithWeekday(date, tz, lc, hr12);
        case 'DayMonthYear':
          return formatDate(date, tz, lc, hr12);
        case 'DayMonthYearHourMinutes':
          return formatDateAndTime(date, tz, lc, hr12);
        case 'DayMonthYearHourMinuteSeconds':
          return formatDateAndTimeWithSeconds(date, tz, lc, hr12);
        case 'HourMinuteSeconds':
          return formatTimeWithSeconds(date, tz, lc, hr12);
        case 'ShortDayMonthYearHourMinutes':
          return formatShortDateAndTime(date, tz, lc, hr12);
        case 'CompactDayMonthYearHourMinutes':
          return formatCompactDateAndTime(date, tz, lc, hr12);
        case 'ISO':
          return formatISO(date, tz);
        case 'Timecode':
          return formatTimecode(date, tz, lc, hr12);
        case 'TimecodeShort':
          return formatTimecodeShort(date, tz, lc, hr12);
        case 'FullDateTimeWithTimezone':
          return formatFullDateTimeWithTimezone(date, tz, lc, hr12);
        case 'CompactDate':
          return formatCompactDate(date, tz, lc, hr12);
        case 'ShortDate':
          return formatShortDate(date, tz, lc, hr12);
        default:
          return tz
            ? moment(date).tz(tz).format(format)
            : moment(date).format(format);
      }
    }
    return formatDateAndTime(date, tz, lc, hr12);
  }

  setTimezone(timezone: string | undefined) {
    this.timezone = timezone;
  }

  setLocale(locale: string | undefined) {
    this.locale = locale;
  }

  setTenantTimeFormat(format: TenantTimeFormat | undefined | null) {
    this.tenantTimeFormat = format ?? undefined;
  }

  /**
   * Get the effective time format being used for time display.
   * Returns the tenant-configured format if explicitly set to '12h' or '24h',
   * otherwise detects the format from the browser locale by formatting a test date.
   *
   * @returns '12h' if 12-hour format with AM/PM should be used, '24h' for 24-hour format
   */
  getEffectiveTimeFormat(): '12h' | '24h' {
    const tenantFormat = this.tenantTimeFormat;

    if (tenantFormat === TimeFormat.TWENTY_FOUR_HOUR) {
      return TimeFormat.TWENTY_FOUR_HOUR;
    }
    if (tenantFormat === TimeFormat.TWELVE_HOUR) {
      return TimeFormat.TWELVE_HOUR;
    }

    const locale = this.locale || undefined;
    const dtf = new Intl.DateTimeFormat(locale, { hour: 'numeric' });
    const hourCycle = dtf.resolvedOptions().hourCycle;
    return hourCycle === 'h11' || hourCycle === 'h12' ? TimeFormat.TWELVE_HOUR : TimeFormat.TWENTY_FOUR_HOUR;
  }

  /**
   * Formats a date as relative time from now (e.g., "2 hours ago", "3 days ago")
   * Uses Intl.RelativeTimeFormat for localized output.
   *
   * @param date - The date to format (can be string, number, or Date)
   * @returns Localized relative time string, or "Invalid date" if date is invalid
   */
  formatRelativeTime(date: string | number | Date): string {
    if (date === undefined) {
      date = new Date();
    }

    if (!isValidDateValue(date)) {
      return 'Invalid date';
    }

    const targetDate = new Date(date);
    const now = new Date();
    const diffInMs = targetDate.getTime() - now.getTime();
    const diffInSeconds = Math.round(diffInMs / 1000);
    const diffInMinutes = Math.round(diffInSeconds / 60);
    const diffInHours = Math.round(diffInMinutes / 60);
    const diffInDays = Math.round(diffInHours / 24);
    const diffInMonths = Math.round(diffInDays / 30);
    const diffInYears = Math.round(diffInDays / 365);

    const locale = this.locale || undefined;
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

    if (Math.abs(diffInSeconds) < 60) {
      return rtf.format(diffInSeconds, 'second');
    } else if (Math.abs(diffInMinutes) < 60) {
      return rtf.format(diffInMinutes, 'minute');
    } else if (Math.abs(diffInHours) < 24) {
      return rtf.format(diffInHours, 'hour');
    } else if (Math.abs(diffInDays) < 30) {
      return rtf.format(diffInDays, 'day');
    } else if (Math.abs(diffInMonths) < 12) {
      return rtf.format(diffInMonths, 'month');
    } else {
      return rtf.format(diffInYears, 'year');
    }
  }

  /**
   * Use this when timeString is a utc iso string and you want to convert
   * it to the configured timezone (most likely for presentaton in the UI)
   * @param timeString utc iso string or undefined (undefined is equivalent to 'now')
   */
  date(timeString: string | undefined): moment.Moment {
    return this.timezone
      ? moment(timeString).tz(this.timezone)
      : moment(timeString);
  }

  /**
   * Use this when a formatted date string is given as input which should be considered
   * as given in a configured timezone (has no timezone indicador in the string itself).
   * example: '30/10/2022 10:55'
   * @param formattedDateString formatted date string
   */
  dateFromFormattedString(formattedDateString: string) {
    return this.timezone
      ? moment.tz(formattedDateString, this.timezone)
      : moment(formattedDateString);
  }

  /**
   * Returns a moment date object from the given time in milliseconds since epoch
   */
  dateFromMilliseconds(ms: number) {
    return this.timezone ? moment(ms).tz(this.timezone) : moment(ms);
  }
}

/**
 * Intl.DateTimeFormat does not have an ISO option, so the
 * recommended way is to use the Swedish locale to control
 * the formatting, and insert your own timezone. To get standard
 * ISO this should be used with the UTC timezone, but supports
 * any timezone. We separate the format to parts to add the T
 * as per the ISO standard.
 */
function formatISO(date: string | number | Date, timeZone?: string) {
  const parts = Intl.DateTimeFormat('sv-SE', {
    timeZone: timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZoneName: 'short',
  }).formatToParts(new Date(date));
  const year = parts.find((e) => e.type === 'year')?.value;
  const month = parts.find((e) => e.type === 'month')?.value;
  const day = parts.find((e) => e.type === 'day')?.value;
  const hour = parts.find((e) => e.type === 'hour')?.value;
  const minute = parts.find((e) => e.type === 'minute')?.value;
  const second = parts.find((e) => e.type === 'second')?.value;
  const isoString = `${year}-${month}-${day}T${hour}:${minute}:${second}`;
  return isoString;
}

function formatDate(
  date: string | number | Date,
  timeZone?: string,
  locale?: string,
  hour12?: boolean
) {
  return Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour12: hour12,
    timeZone: timeZone,
  }).format(new Date(date));
}

function formatDateAndTime(
  date: string | number | Date,
  timeZone?: string,
  locale?: string,
  hour12?: boolean
) {
  return Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: hour12,
    timeZone: timeZone,
  }).format(new Date(date));
}

function formatShortDateAndTime(
  date: string | number | Date,
  timeZone?: string,
  locale?: string,
  hour12?: boolean
) {
  return Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: hour12,
    timeZone: timeZone,
  }).format(new Date(date));
}

function formatCompactDateAndTime(
  date: string | number | Date,
  timeZone?: string,
  locale?: string,
  hour12?: boolean
) {
  return Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: '2-digit',
    hour: 'numeric',
    minute: 'numeric',
    hour12: hour12,
    timeZone: timeZone,
  }).format(new Date(date));
}

function formatDateAndTimeWithWeekday(
  date: string | number | Date,
  timeZone?: string,
  locale?: string,
  hour12?: boolean
) {
  return Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: hour12,
    timeZone: timeZone,
  }).format(new Date(date));
}

function formatShortDateAndTimeWithWeekday(
  date: string | number | Date,
  timeZone?: string,
  locale?: string,
  hour12?: boolean
) {
  return Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: hour12,
    timeZone: timeZone,
  }).format(new Date(date));
}

function formatDateAndTimeWithSeconds(
  date: string | number | Date,
  timeZone?: string,
  locale?: string,
  hour12?: boolean
) {
  return Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: hour12,
    timeZone: timeZone,
  }).format(new Date(date));
}

function formatCompactDate(
  date: string | number | Date,
  timeZone?: string,
  locale?: string,
  hour12?: boolean
) {
  return Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: timeZone,
    hour12: hour12,
  }).format(new Date(date));
}

function formatShortDate(
  date: string | number | Date,
  timeZone?: string,
  locale?: string,
  hour12?: boolean
) {
  return Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    timeZone: timeZone,
    hour12: hour12,
  }).format(new Date(date));
}

function formatTimeWithSeconds(
  date: string | number | Date,
  timeZone?: string,
  locale?: string,
  hour12?: boolean
) {
  return Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: hour12,
    timeZone: timeZone,
  }).format(new Date(date));
}

function formatTimecode(
  date: string | number | Date,
  timeZone?: string,
  locale?: string,
  hour12?: boolean
) {
  const parts = Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    timeZone: timeZone,
    hour12: hour12,
  })
    .formatToParts(new Date(date));
  if (hour12) {
    return parts.map((item) => item.value).join('');
  }
  return parts.filter((item) => item.type !== 'dayPeriod')
    .map((item) => item.value)
    .join('');
}

function formatTimecodeShort(
  date: string | number | Date,
  timeZone?: string,
  locale?: string,
  hour12?: boolean
) {
  const parts = Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: 'numeric',
    timeZone: timeZone,
    hour12: hour12,
  }).formatToParts(new Date(date));
  if (hour12) {
    return parts.map((item) => item.value).join('');
  }
  return parts.filter((item) => item.type !== 'dayPeriod')
    .map((item) => item.value)
    .join('');
}

function formatFullDateTimeWithTimezone(
  date: string | number | Date,
  timeZone?: string,
  locale?: string,
  hour12?: boolean
) {
  return Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    timeZoneName: 'short',
    hour12: hour12,
    timeZone: timeZone,
  }).format(new Date(date));
}
